import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  Skeleton,
  Switch,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../components/teacher-picker';
import { EventFilters } from '../components/event-filters';

/**
 * One card on this page. Besides the class/event identity it carries the
 * delivery counts: totalStudents, studentsWithSubmissions, submittedExercises,
 * pendingReview (written/recorded work without a teacher comment yet) and
 * lastSubmittedAt.
 */
type HomeworkClassItem = RouterOutputs['teacher']['events']['homeworkClasses']['classes'][number];

const timeOf = (date: Date | string | null) => (date ? new Date(date).getTime() : 0);

/**
 * Classes with work waiting for the teacher come first. With the "only pending
 * review" toggle on, the busiest class leads and ties break by newest delivery;
 * otherwise the server order is kept inside each group (stable sort).
 */
function orderClasses(classes: HomeworkClassItem[], byPendingReview: boolean): HomeworkClassItem[] {
  return [...classes].sort((a, b) => {
    if (byPendingReview) {
      if (b.pendingReview !== a.pendingReview) return b.pendingReview - a.pendingReview;
      return timeOf(b.lastSubmittedAt) - timeOf(a.lastSubmittedAt);
    }
    return Number(b.pendingReview > 0) - Number(a.pendingReview > 0);
  });
}

export const Route = createFileRoute('/homework')({
  component: HomeworkListRoute,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function HomeworkListRoute() {
  const [groupId, setGroupId] = useState<string>('all');
  const [studentSearch, setStudentSearch] = useState<string>('');
  const [onlyWithDeliveries, setOnlyWithDeliveries] = useState(false);
  const [onlyPendingReview, setOnlyPendingReview] = useState(false);
  const { t } = useTranslation('admin');
  const teacherId = useTeacherId();

  // First fetch without studentId to get the full student list for search matching
  const { data: baseData } = trpc.teacher.events.homeworkClasses.useQuery({
    teacherId,
    groupId: groupId !== 'all' ? groupId : undefined,
  });

  const groupOptions = baseData?.filters.groups ?? [];
  const studentOptions = baseData?.filters.students ?? [];

  // Derive matched studentId from text search
  const matchedStudent = useMemo(() => {
    const search = studentSearch.toLowerCase().trim();
    if (!search) return undefined;
    const matches = studentOptions.filter(
      (s) => s.label.toLowerCase().includes(search) || s.email?.toLowerCase().includes(search),
    );
    return matches.length === 1 ? matches[0] : undefined;
  }, [studentSearch, studentOptions]);
  const matchedStudentId = matchedStudent?.studentId;

  // When a single student matches, fetch filtered results from backend
  const { data, isLoading } = trpc.teacher.events.homeworkClasses.useQuery({
    teacherId,
    groupId: groupId !== 'all' ? groupId : undefined,
    studentId: matchedStudentId,
  });

  const allClasses = data?.classes ?? [];
  const classes = useMemo(() => {
    const visible = allClasses.filter(
      (item) =>
        (!onlyWithDeliveries || item.studentsWithSubmissions > 0) &&
        (!onlyPendingReview || item.pendingReview > 0),
    );
    return orderClasses(visible, onlyPendingReview);
  }, [allClasses, onlyWithDeliveries, onlyPendingReview]);
  const totalClasses = classes.length;
  const filtersHideEverything = allClasses.length > 0 && classes.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">{t('teacher.homeworkPage.eyebrow')}</p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.homeworkPage.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.homeworkPage.description')}</p>
        </div>
        <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
          {t('teacher.homeworkPage.badgeLabel', { count: totalClasses })}
        </Badge>
      </div>

      <EventFilters
        groupId={groupId}
        onGroupChange={setGroupId}
        groups={groupOptions}
        studentSearch={studentSearch}
        onStudentSearchChange={setStudentSearch}
      />

      <div className="flex flex-wrap gap-x-6 gap-y-3 rounded-xl border border-rose-50 bg-white p-4 shadow-sm">
        <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-700">
          <Switch
            checked={onlyWithDeliveries}
            onCheckedChange={setOnlyWithDeliveries}
            className="data-[state=checked]:bg-[#cf2e2e]"
          />
          {t('teacher.homeworkPage.onlyWithDeliveries')}
        </label>
        <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-700">
          <Switch
            checked={onlyPendingReview}
            onCheckedChange={setOnlyPendingReview}
            className="data-[state=checked]:bg-[#cf2e2e]"
          />
          {t('teacher.homeworkPage.onlyPendingReview')}
        </label>
      </div>

      {matchedStudent && (
        <p className="text-sm text-gray-600">
          {t('teacher.homeworkPage.studentOnlyHint', { name: matchedStudent.label })}
        </p>
      )}

      {isLoading && <HomeworkListSkeleton />}

      {!isLoading && allClasses.length === 0 && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.homeworkPage.emptyTitle')}</CardTitle>
            <CardDescription>{t('teacher.homeworkPage.emptyDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && filtersHideEverything && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.homeworkPage.noMatchTitle')}</CardTitle>
            <CardDescription>{t('teacher.homeworkPage.noMatchDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {classes.map((item) => (
          <ClassHomeworkCard key={`${item.classId}-${item.groupId ?? 'none'}`} item={item} />
        ))}
      </div>
    </div>
  );
}

function ClassHomeworkCard({ item }: { item: HomeworkClassItem }) {
  const { t } = useTranslation('admin');
  const lastHeldLabel = useMemo(() => {
    if (!item.lastHeldAt) return t('common.fallbacks.noDate');
    return dateFormatter.format(new Date(item.lastHeldAt));
  }, [item.lastHeldAt, t]);
  const lastDeliveryLabel = item.lastSubmittedAt ? dateFormatter.format(new Date(item.lastSubmittedAt)) : null;
  const deliveredPercent =
    item.totalStudents > 0 ? Math.round((item.studentsWithSubmissions / item.totalStudents) * 100) : 0;

  return (
    <Card className="border border-rose-50 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="border-rose-100 bg-rose-50 text-[#cf2e2e]">
            {item.groupLabel}
          </Badge>
          {/* <span className="text-xs uppercase tracking-wide text-gray-400">Última aula</span> */}
        </div>
        <CardTitle className="text-2xl text-gray-900">
          {item.className ?? t('common.fallbacks.class')}
        </CardTitle>
        <CardDescription className="text-sm text-gray-600">
          {item.eventTitle ?? t('common.fallbacks.event')} • {lastHeldLabel}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-700">
              {t('teacher.homeworkPage.deliveredLabel', {
                delivered: item.studentsWithSubmissions,
                total: item.totalStudents,
              })}
            </p>
            {item.pendingReview > 0 ? (
              <Badge className="rounded-full border-transparent bg-[#cf2e2e] text-white hover:bg-[#cf2e2e]">
                {t('teacher.homeworkPage.pendingReview', { count: item.pendingReview })}
              </Badge>
            ) : item.studentsWithSubmissions > 0 ? (
              <Badge variant="outline" className="rounded-full border-emerald-200 bg-emerald-50 text-emerald-700">
                {t('teacher.homeworkPage.nothingToReview')}
              </Badge>
            ) : (
              <Badge variant="outline" className="rounded-full border-gray-200 bg-gray-50 text-gray-500">
                {t('teacher.homeworkPage.noDeliveries')}
              </Badge>
            )}
          </div>
          <Progress
            value={deliveredPercent}
            className="h-1.5 bg-rose-50"
            indicatorClassName="bg-[#cf2e2e]"
            aria-label={t('teacher.homeworkPage.deliveredLabel', {
              delivered: item.studentsWithSubmissions,
              total: item.totalStudents,
            })}
          />
          {lastDeliveryLabel && (
            <p className="text-xs text-gray-500">{t('teacher.homeworkPage.lastDelivery', { date: lastDeliveryLabel })}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          <Badge variant="outline" className="rounded-full border-gray-200 bg-gray-50 text-gray-700">
            {t('teacher.homeworkPage.blocksLabel', { count: item.totalBlocks })}
          </Badge>
          <Badge variant="outline" className="rounded-full border-gray-200 bg-gray-50 text-gray-700">
            {t('teacher.homeworkPage.exercisesLabel', { count: item.totalExercises })}
          </Badge>
          {item.submittedExercises > 0 && (
            <Badge variant="outline" className="rounded-full border-rose-100 bg-rose-50 text-[#cf2e2e]">
              {t('teacher.homeworkPage.submittedExercisesLabel', { count: item.submittedExercises })}
            </Badge>
          )}
        </div>
        <Button asChild className="rounded-full">
          <Link to="/classes/$classId/homework" params={{ classId: item.classId }} search={{ eventId: item.eventId }}>
            {t('teacher.homeworkPage.reviewHomework')}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function HomeworkListSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={`homework-skeleton-${index}`} className="border border-rose-50">
          <CardHeader className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

